import { useState } from 'react';
import { ChevronDown, ChevronRight, Download, Search } from 'lucide-react';
import { DataTable, type Column } from '@/shared/ui';
import { useDataTable } from '@/shared/hooks/useDataTable';
import { fmtDay, money } from '@/shared/formatters';
import { exportExcel } from '@/lib/exportData';
import type { ProcurementOrderRow } from '@contracts/procurement';
import { SlaBadge, StateBadge, UrgencyBadge } from './StatusBadges';
import { SLA_LABEL, STATE_LABEL, URGENCY_LABEL } from './procurementMeta';

/** Catálogo ÚNICO de columnas; cada sección elige las suyas por key. */
const ALL_COLUMNS: Column<ProcurementOrderRow>[] = [
  { key: 'name', header: 'Folio', sortable: true, render: (o) => <span className="font-semibold text-slate-700">{o.name}</span> },
  { key: 'requester', header: 'Solicitante', sortable: true, render: (o) => o.requester },
  { key: 'company', header: 'Compañía', sortable: true, render: (o) => o.company ?? '—' },
  { key: 'department', header: 'Depto.', sortable: true, render: (o) => o.department ?? '—' },
  { key: 'partner', header: 'Proveedor', sortable: true, render: (o) => o.partner ?? '—' },
  { key: 'createdAt', header: 'Creada', sortable: true, render: (o) => fmtDay(o.createdAt) },
  { key: 'authorizedAt', header: 'Autorizada', sortable: true, render: (o) => (o.authorizedAt ? fmtDay(o.authorizedAt) : '—') },
  { key: 'ageDays', header: 'Días', align: 'right', sortable: true, render: (o) => o.ageDays },
  { key: 'amount', header: 'Monto', align: 'right', sortable: true, render: (o) => money(o.amount) },
  { key: 'rejectionReason', header: 'Motivo', render: (o) => <span className="text-slate-500">{o.rejectionReason ?? '—'}</span> },
  { key: 'state', header: 'Estatus', sortable: true, render: (o) => <StateBadge state={o.state} /> },
  { key: 'urgency', header: 'Urgencia', sortable: true, render: (o) => <UrgencyBadge urgency={o.urgency} /> },
  { key: 'slaState', header: 'SLA', sortable: true, render: (o) => <SlaBadge sla={o.slaState} /> },
];

function sortValue(o: ProcurementOrderRow, key: string): string | number | undefined {
  switch (key) {
    case 'amount':
      return o.amount;
    case 'ageDays':
      return o.ageDays;
    default:
      return (o as unknown as Record<string, string | number | undefined>)[key];
  }
}

const PAGE_SIZE = 25;

/** Fila → renglón de Excel con etiquetas legibles (respeta filtros/búsqueda). */
function toExportRow(o: ProcurementOrderRow): Record<string, string | number> {
  return {
    Folio: o.name,
    'Compañía': o.company ?? '',
    Solicitante: o.requester,
    Departamento: o.department ?? '',
    Proveedor: o.partner ?? '',
    Creada: fmtDay(o.createdAt),
    Autorizada: o.authorizedAt ? fmtDay(o.authorizedAt) : '',
    'Días': o.ageDays,
    Monto: o.amount,
    Estatus: STATE_LABEL[o.state],
    Urgencia: URGENCY_LABEL[o.urgency],
    SLA: SLA_LABEL[o.slaState],
    Motivo: o.rejectionReason ?? '',
  };
}

export interface PurchaseTableProps {
  title: string;
  rows: ProcurementOrderRow[];
  loading?: boolean;
  onRowClick: (o: ProcurementOrderRow) => void;
  /** Keys de `ALL_COLUMNS` a mostrar (en ese orden). Ausente = todas. */
  columnKeys?: string[];
  /** Punto de color del header (identidad visual de la sección). */
  accentDot?: string;
  /** Sección colapsable; arranca según `defaultOpen`. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  emptyText?: string;
}

export function PurchaseTable({
  title,
  rows,
  loading,
  onRowClick,
  columnKeys,
  accentDot = 'bg-slate-400',
  collapsible = false,
  defaultOpen = true,
  emptyText = 'Sin órdenes para los filtros actuales.',
}: PurchaseTableProps) {
  const [open, setOpen] = useState(defaultOpen);
  const columns = columnKeys
    ? columnKeys
        .map((k) => ALL_COLUMNS.find((c) => c.key === k))
        .filter((c): c is Column<ProcurementOrderRow> => Boolean(c))
    : ALL_COLUMNS;

  const table = useDataTable<ProcurementOrderRow>({
    items: rows,
    search: (o) => [o.name, o.requester, o.department, o.partner, o.company, o.rejectionReason],
    sortValue,
    initialSortKey: 'createdAt',
    initialSortDir: 'asc',
    pageSize: PAGE_SIZE,
  });

  const subtotal = rows.reduce((s, o) => s + o.amount, 0);
  const bodyVisible = !collapsible || open;

  return (
    <section className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <button
          type="button"
          onClick={collapsible ? () => setOpen(!open) : undefined}
          aria-expanded={collapsible ? open : undefined}
          className={`flex items-center gap-2 text-left ${collapsible ? '' : 'cursor-default'}`}
        >
          {collapsible &&
            (open ? (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-400" />
            ))}
          <span className={`h-2.5 w-2.5 rounded-full ${accentDot}`} />
          <h3 className="text-sm font-bold text-slate-700">
            {title} <span className="font-semibold text-slate-400">({table.total})</span>
          </h3>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
            {money(subtotal)}
          </span>
        </button>
        {bodyVisible && (
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={table.query}
                onChange={(e) => table.setQuery(e.target.value)}
                placeholder="Buscar…"
                className="w-44 rounded-lg border border-slate-300 py-1.5 pl-8 pr-2 text-sm focus:border-red-400 focus:outline-none"
              />
            </div>
            <button
              onClick={() => void exportExcel(title.toLowerCase().replace(/[^a-z0-9]+/g, '-'), table.all.map(toExportRow))}
              disabled={table.total === 0}
              title="Exportar a Excel (respeta la búsqueda)"
              className="rounded-lg border border-slate-300 p-1.5 text-slate-500 hover:border-emerald-400 hover:text-emerald-600 disabled:opacity-40"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        )}
      </header>

      {bodyVisible && (
        <>
          <div className="max-h-[60vh] overflow-auto">
            <DataTable
              columns={columns}
              rows={table.rows}
              rowKey={(o) => o.id}
              sortKey={table.sortKey}
              sortDir={table.sortDir}
              onSort={table.toggleSort}
              onRowClick={onRowClick}
              loading={loading}
              loadingText="Cargando órdenes…"
              emptyText={emptyText}
            />
          </div>

          {table.pageCount > 1 && (
            <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
              <button
                onClick={() => table.setPage(table.page - 1)}
                disabled={table.page === 0}
                className="rounded-lg px-2.5 py-1 font-semibold hover:bg-slate-100 disabled:opacity-40"
              >
                ‹
              </button>
              <span>
                {table.page + 1} / {table.pageCount}
              </span>
              <button
                onClick={() => table.setPage(table.page + 1)}
                disabled={table.page >= table.pageCount - 1}
                className="rounded-lg px-2.5 py-1 font-semibold hover:bg-slate-100 disabled:opacity-40"
              >
                ›
              </button>
            </footer>
          )}
        </>
      )}
    </section>
  );
}
